import { Link } from '@tanstack/react-router';
import { useGT } from 'gt-react';
import { formatCurrency } from '@erp/shared-utils';
import { useOpenDraftPurchaseOrders } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { draftPurchaseOrderRouteSearch } from '../../purchase-orders/adHocPurchaseOrderNavigation';
import { openDraftLabel, type OpenDraftOrder } from '../utils/openDraftOrders';

function useOpenDraftStatusLabel() {
  const gt = useGT();
  return (status: OpenDraftOrder['status']) =>
    status === 'pending_approval'
      ? gt('awaiting approval', { context: 'open draft purchase order status' })
      : gt('approved', { context: 'open draft purchase order status' });
}

/**
 * Unsent draft POs for the open cart's vendor × ship-to, as small links to the
 * draft on the Replenishment Orders page. Released lines leave this board, so
 * this is where a buyer finds what they were released into.
 */
export function OpenDraftPurchaseOrders({
  supplierId,
  shipToWarehouseId,
}: {
  supplierId: string;
  shipToWarehouseId: string;
}) {
  const gt = useGT();
  const statusLabel = useOpenDraftStatusLabel();
  const { data } = useOpenDraftPurchaseOrders({
    supplierId,
    shipToWarehouseId,
  });
  const orders = data?.orders ?? [];
  if (orders.length === 0) return null;
  return (
    <span className="flex min-w-0 items-center gap-2 overflow-hidden text-muted-foreground">
      {orders.map((order) => (
        <Link
          key={order.id}
          to="/procurement/replenishment/orders"
          search={draftPurchaseOrderRouteSearch(order.id)}
          className="shrink-0 underline-offset-2 hover:underline"
          title={gt('Open this draft purchase order', {
            context: 'open draft purchase order link tooltip',
          })}
        >
          {gt('Open PO #{label}', {
            context: 'open draft purchase order link',
            label: openDraftLabel(order),
          })}{' '}
          <span className="tabular-nums">
            {formatCurrency(order.totalAmount)}
          </span>{' '}
          · {statusLabel(order.status)}
        </Link>
      ))}
    </span>
  );
}
